<template>
  <EInstantAnswer
    class="flight-booking"
    :sources="sources"
    data-test-id="flight-booking"
    :feedback-link="{
      ticketSubject: 'Search - Flight Booking',
      widgetType: 'flight_booking_rich',
    }"
    :sr-title="$t('web.widgets.flights.title')"
    powered-by-ecosia
  >
    <template #header>
      <component :is="logo" class="flight-booking__logo" />
    </template>
    <ELoading
      v-show="isLoading"
      class="flight-booking__loading"
      data-test-id="flight-booking-loading"
    />
    <form
      v-if="!isLoading"
      method="GET"
      data-test-id="flight-booking-form"
      :action="submitUrl"
      @submit="submit"
      @formdata="formdata"
      @focusin="trackWidgetFocus"
    >
      <div class="flight-booking__header">
        <div class="header__select-details">
          <EFilter
            class="flight-booking__filter"
            name="numberofadults"
            :hint="$t('web.widgets.flights.numberofadults')"
            size="m"
            side="right"
            type="select"
            data-test-id="flight-booking-adults"
            :options="adultOptions"
            :selected="numAdults"
            @select="value => numAdults = value"
          />
          <input type="hidden" name="adultsv2" :value="numAdults">
          <EFilter
            class="flight-booking__filter"
            name="triptype"
            data-test-id="flight-trip-type"
            :hint="$t('web.widgets.flights.triptype')"
            size="m"
            side="right"
            type="select"
            :options="tripTypeOptions"
            :selected="tripType"
            @select="value => tripType = value"
          />
          <input type="hidden" name="rtn" :value="isRoundtrip ? 1 : 0">
        </div>
      </div>
      <div class="flight-booking__main">
        <p v-if="error" class="flight-main__error" data-test-id="flight-booking-error">
          {{ error }}
        </p>
        <div
          v-on-clickaway="closeSuggestions"
          class="flight-booking-form__dropdown-wrapper"
        >
          <EDynamicSelect
            :key="swapped ? 'origin' : 'origin-swapped'"
            :value="origin ? origin.entityId : ''"
            :options="[origin]"
            class="flight-booking-form__item flight-booking-form__input"
            name="origin"
            :placeholder="$t('web.widgets.flights.departingfrom')"
            :aria-label="$t('web.widgets.flights.departingfrom')"
            :search-function="searchLocation"
            options-shown="7"
            data-test-id="flight-booking-origin"
            @select="(value) => origin = value"
          >
            <template #list-item="slotProps">
              <SuggestionItem
                :key="slotProps.option.entityId"
                v-bind="slotProps.option"
              />
            </template>
          </EDynamicSelect>
          <EButton
            ref="swap"
            class="flight-booking-form__item flight-booking-form__item--swap"
            :variant="isTabletUp ? 'outline' : 'bare'"
            icon="arrow-exchange"
            data-test-id="flight-booking-swap"
            @click="swapLocations"
          />
          <EDynamicSelect
            :key="swapped ? 'destination' : 'destination-swapped'"
            :value="destination ? destination.entityId : ''"
            :options="[destination]"
            class="flight-booking-form__item flight-booking-form__input"
            name="destination"
            :placeholder="$t('web.widgets.flights.arrivingto')"
            :aria-label="$t('web.widgets.flights.arrivingto')"
            :search-function="searchLocation"
            options-shown="7"
            data-test-id="flight-booking-destination"
            @select="(value) => destination = value"
          >
            <template #list-item="slotProps">
              <SuggestionItem
                :key="slotProps.option.entityId"
                v-bind="slotProps.option"
              />
            </template>
          </EDynamicSelect>
        </div>
        <div class="flight-booking-form__dates-submit-wrapper">
          <ESegmentedInput
            class="flight-booking-form__input flight-booking-form__item"
          >
            <EInput
              v-model="outboundDate"
              class="flight-booking-form__date"
              type="date"
              name="outboundDate"
              :min="todayDate"
              :max="oneYearLaterDate"
              :aria-label="$t('web.widgets.flights.departuredate')"
              icon="chevron-down"
              icon-size="s"
              :icon-start="isTabletUp ? 'date' : ''"
              data-test-id="flight-booking-outbound-date"
              required
            />
            <EInput
              v-if="isRoundtrip"
              v-model="inboundDate"
              class="flight-booking-form__date"
              type="date"
              name="inboundDate"
              :min="outboundDate"
              :max="oneYearLaterDate"
              :aria-label="$t('web.widgets.flights.arrivaldate')"
              icon="chevron-down"
              icon-size="s"
              data-test-id="flight-booking-inbound-date"
              required
            />
          </ESegmentedInput>
          <!-- TODO: check the button size for desktop
            once segmented date picker is implemented -->
          <EButton
            type="submit"
            variant="outline"
            class="flight-booking-form__item flight-booking-form__item--submit"
          >
            {{ $t('web.widgets.flights.button') }}
          </EButton>
        </div>
      </div>
    </form>
  </EInstantAnswer>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapState } from 'vuex';

import {
  trackImpression,
  trackClick,
  PAGE_SERP,
  TYPE_RICH,
} from '~~/search/common/js/client/analytics/rich-content';
import screen from '~~/common/vue/mixins/screen';
import EInstantAnswer from '~/components/instant-answers/base';
import EInput from '~~/common/vue/components/input';
import ESegmentedInput from '~~/common/vue/components/input/segmented-input';
import EButton from '~~/common/vue/components/button';
import EDynamicSelect from '~~/common/vue/components/select/dynamic-select';
import EFilter from '~~/common/vue/components/filter';
import ELoading from '~~/common/vue/components/loading';

import { getInitialDates } from './dates';
import SuggestionItem from './suggestionItem';
import extractLocations from './extract-locations';

import {
  requestSuggestions,
  CABIN_CLASS_ECONOMY,
  getReferralsUrl,
  MEDIA_PARTNER_ID,
} from './skyscanner';

export const TRACK_ID = 'flight_booking';
const TYPE_ROUNDTRIP = 'roundtrip';
const TYPE_ONEWAY = 'oneway';

const dropDownOptions = (singular, plural) => {
  const options = [1, 2, 3, 4, 5].map((option) => ({ value: `${option}`, label: `${option} ${plural}` }));
  options[0].label = `1 ${singular}`;
  return options;
};

export default {
  name: 'EFlightBooking',
  components: {
    EInstantAnswer,
    EInput,
    EButton,
    SuggestionItem,
    EDynamicSelect,
    ESegmentedInput,
    EFilter,
    ELoading,
  },
  mixins: [clickaway, screen],
  data() {
    const { today, oneYearLater, outbound, inbound } = getInitialDates();

    return {
      hasInteracted: false,
      isLoading: true,
      origin: '',
      destination: '',
      numAdults: '1',
      numChildren: '0',
      outboundDate: outbound,
      inboundDate: inbound,
      todayDate: today,
      oneYearLaterDate: oneYearLater,
      cabinClass: CABIN_CLASS_ECONOMY, // we don't support other values atm
      tripType: TYPE_ROUNDTRIP,
      error: '',
      submitUrl: '',
      swapped: false, // Necessary to break the select model
    };
  },
  computed: {
    ...mapState('user', ['bingMarketCode']),
    ...mapState('global', ['locale']),
    sources() {
      return [
        {
          title: 'Skyscanner',
          url: 'https://www.skyscanner.net/',
        },
      ];
    },
    locations() {
      const query = this.$route.query.q?.toString();
      if (!query) {
        return {};
      }

      return extractLocations(query, this.locale);
    },
    adultOptions() {
      return dropDownOptions(
        this.$t('web.widgets.flights.adult'),
        this.$t('web.widgets.flights.adults'),
      );
    },
    childOptions() {
      return dropDownOptions(
        this.$t('web.widgets.flights.child'),
        this.$t('web.widgets.flights.children'),
      );
    },
    tripTypeOptions() {
      return [
        { value: TYPE_ROUNDTRIP, label: this.$t('web.widgets.flights.roundtrip') },
        { value: TYPE_ONEWAY, label: this.$t('web.widgets.flights.oneway') },
      ];
    },
    logo() {
      return () => import(
        /* webpackMode: "lazy" */
        '~~/search/web/src/assets/img/skyscanner.svg'
      );
    },
    isRoundtrip() {
      return this.tripType === TYPE_ROUNDTRIP;
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
      once: false,
    });

    this.preloadLocations();
  },
  methods: {
    async preloadLocations() {
      // Preload locations
      const { origin, destination } = this.locations;
      if (origin) {
        const [suggestedOrigin] = await this.searchLocation(origin, 1);
        this.origin = suggestedOrigin;
      }
      if (destination) {
        const [suggestedDestination] = await this.searchLocation(destination, 1);
        this.destination = suggestedDestination;
      }
      this.isLoading = false;
    },
    closeSuggestions() {
      this.showOriginDropdown = false;
      this.showDestinationDropdown = false;
    },
    async searchLocation(searchTerm, maxResults) {
      const data = await requestSuggestions(
        searchTerm,
        this.locale,
        this.bingMarketCode,
        maxResults,
      );
      return data.places.map((suggestion) => ({
        ...suggestion,
        value: suggestion.entityId,
        label: suggestion.name,
      }));
    },
    swapLocations() {
      const { origin } = this;
      this.origin = this.destination;
      this.destination = origin;
      this.swapped = !this.swapped;
      this.$nextTick(() => {
        this.$refs.swap.$el.focus();
      });
    },
    /**
     * Validates the data and selects the appropriate
     * target URL based on the provided data.
     *
     * @param {SubmitEvent} event
     */
    submit(event) {
      if (this.origin && this.destination) {
        if (this.outboundDate && this.inboundDate) {
          // We have all the info for a return flight
          this.submitUrl = getReferralsUrl('dayView');
        } else {
          // We know where to go but not sure when
          this.submitUrl = getReferralsUrl('monthView');
        }
      } else if (this.origin && !this.destination) {
        // We want to search for any flights from the origin
        this.submitUrl = getReferralsUrl('browseView');
      } else if (!this.origin && this.destination) {
        // We want to search for flights from anywhere to our destination
        this.submitUrl = getReferralsUrl('cheapFlightsTo');
      } else {
        this.error = this.$t('web.widgets.flights.error.missingoriginordestination');
        event.preventDefault();
        return false;
      }

      this.trackFormSubmit();

      // Remove null data from form
      return true;
    },
    /**
     * Before submitting the form data, we need to remove any null or empty values.
     * The API doesn't accept null values.
     * Some values also need some-preprocessing.
     *
     * @param {FormDataEvent} event
     */
    formdata({ formData }) {
      for (const key of formData.keys()) {
        if (!formData.get(key)) {
          formData.delete(key);
        }
      }

      if (!this.isRoundtrip) {
        formData.delete('inboundDate');
      }

      if (this.origin) {
        formData.set('origin', this.origin.iataCode);
      }
      if (this.destination) {
        formData.set('destination', this.destination.iataCode);
      }

      formData.set('mediaPartnerId', MEDIA_PARTNER_ID);
    },
    trackWidgetFocus() {
      // Track first interaction with the form
      // Not just clicking but also via keyboard navigation
      if (this.hasInteracted) {
        return;
      }
      this.hasInteracted = true;
      const track = trackClick({
        name: TRACK_ID,
        type: TYPE_RICH,
        target: 'form',
      });

      track();
    },
    // Track form submission, either via button click
    // or using Enter key in one of the fields.
    trackFormSubmit: trackClick({
      name: TRACK_ID,
      type: TYPE_RICH,
      target: 'search',
    }),
  },
};
</script>

<style lang="scss" scoped>
.flight-booking {
  position: relative;
  min-height: 230px;
}

.flight-booking__logo {
  color: $skyscanner-sky-blue;
  margin: $space-m $space-m 0;

  .dark & {
    color: $skyscanner-sky-blue-dark-mode;
  }
}

.flight-booking__header {
  display: flex;
  flex-direction: column;

  @include tablet {
    justify-content: flex-end;
    flex-direction: row;
    margin-top: -$space-2l;
  }
}

.header__select-details {
  display: flex;
  flex-direction: row;
}

.flight-booking__main {
  padding-top: $space-m;
}

.flight-booking__error {
  color: var(--color-error);
}

// TODO: probably remove this once passenger dropdown is implemented
.field + .field {
  margin-top: 0;
}

.flight-booking-form__dropdown-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: $space-m;

  @include tablet {
    flex-direction: row;
  }
}

.flight-booking-form__input {
  width: 100%;
}

.flight-booking-form__input:not(:last-child) {
  margin-bottom: $space-m;

  @include tablet {
    margin-bottom: 0;
  }
}

.flight-booking-form__dates-submit-wrapper {
  display: flex;
  flex-direction: column;

  @include tablet {
    flex-direction: row;
    align-items: end;
  }
}

.flight-booking-form__item:not(:last-child) {
  @include tablet {
    margin-right: $space-1s;
  }
}

.flight-booking-form__item--swap {
  position: absolute;
  right: $space-m;
  transform: rotate(90deg);
  z-index: $z-index-l;

  @include tablet {
    position: relative;
    right: 0;
    transform: none;
  }
}

.flight-booking-form__item--submit {
  flex-shrink: 0;
}
</style>

<style lang="scss">
.flight-booking-form__date:focus-within .input__addon--end .icon {
  transform: scaleY(-1);
}

// When navigating with the keyboard, the native date picker doesn't open
// So we want to hide the chevron altogether.
.keyboard-mode .flight-booking-form__date:focus-within .input__addon--end {
  display: none;
}

.flight-booking__filter .filter__content {
  right: 0;
}

.flight-booking__filter.filter--selected .filter__button--highlighted .filter__text {
  font-weight: $font-weight-m;
}

.flight-booking__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: $z-index-l;
}
</style>
